<template>
  <div ref="boxComponent" class="container box_component">
    <!-- Título y botón de nueva competición -->
    <div class="header-title">
      <h2>{{ $t('competitions.title') }}</h2>
      <button v-if="false && isOrganizer(user)" class="new-competition-btn" @click="createNewCompetition">
        <i class="fa-solid fa-plus"></i> {{ $t('competitions.btn_new') }}
      </button>
      <button
          v-if="isOrganizer(user)"
          class="new-competition-btn"
          style="background-color: #4c56af"
          @click="openAssistant"
      >
        <i class="fa-solid fa-magic-wand-sparkles"></i> {{ $t('competitions.assistant.button') }}
      </button>
      <button
          v-if="isTechOperator && isDesktopDevice()"
          class="new-competition-btn tech-preview-btn"
          @click="openTechPreview"
      >
        <i class="fa-solid fa-video"></i> {{ $t('competitions.tech_preview') || 'Vista técnica' }}
      </button>
    </div>

    <!-- Barra de búsqueda y filtros -->
    <div class="filter-bar">
      <input v-model="searchQuery" @input="onSearchInput" type="text" :placeholder="$t('competitions.view.search')" class="search-input">
      <select v-model="selectedYear" class="year-filter" @change="onFilterChange">
        <option value="">{{ $t('competitions.view.year') }}</option>
        <option v-for="year in uniqueYears" :key="year" :value="year">{{ year }}</option>
      </select>
      <select v-model="selectedMonth" class="month-filter" @change="onFilterChange">
        <option value="">{{ $t('competitions.view.month') }}</option>
        <option v-for="month in monthOptions" :key="month.value" :value="month.value">{{ month.label }}</option>
      </select>
      <select v-model="selectedStatus" class="status-filter" @change="onFilterChange">
        <option value="">{{ $t('competitions.view.status') }}</option>
        <option v-for="status in uniqueStatuses" :key="status" :value="status">{{ status }}</option>
      </select>
      <select v-model="selectedRule" class="rule-filter" @change="onFilterChange">
        <option value="">{{ $t('competitions.view.rule') }}</option>
        <option v-for="rule in uniqueRules" :key="rule" :value="rule">{{ rule }}</option>
      </select>
      <select v-model="selectedCountry" class="country-filter" @change="onFilterChange">
        <option value="">{{ $t('competitions.view.country') }}</option>
        <option v-for="country in uniqueCountries" :key="country" :value="country">{{ country }}</option>
      </select>
      <select v-model="selectedType" class="type-filter" @change="onFilterChange">
        <option value="">{{ $t('competitions.view.kind') }}</option>
        <option v-for="type in uniqueTypes" :key="type" :value="type">{{ type }}</option>
      </select>
    </div>

    <!-- Indicador de carga -->
    <p v-if="isLoading" class="loading-text">{{ $t('competitions.loading') }}</p>

    <!-- Lista de competiciones -->
    <div class="competitions-grid" v-if="!isLoading" :class="['competitions-grid', competitionList.length <= 2 ? 'single' : '']">
      <p v-if="competitionList.length === 0" class="no-results">{{ $t('competitions.empty_competition') }}</p>
      <template v-for="competition in competitionList" :key="competition.id" >
          <div class="competition-card" :class="getStatusClass(competition.status_id)" @click.prevent="viewCompetition(competition.id)">
            <div class="card-header" role="button" tabindex="0">
              <img :src="competition.competitions_info?.img_card_path || competition.competitions_info?.img_path || defaultImage" alt="Competition Image" class="card-image" loading="lazy" decoding="async">
              <h3 class="card-title">{{ competition.name }}</h3>
            </div >
            <div class="card-body" role="button" tabindex="0">
              <div class="status-line text-center">
                <span class="status-pill" :class="statusKey(competition.status_id)">{{ competition.status?.name || 'Estado desconocido' }}</span>
              </div>

              <div class="meta-line d-flex justify-content-between align-items-center text-muted small">
                <span class="d-flex align-items-center gap-1">
                  <i class="fa-regular fa-calendar"></i>
                  {{ formatDateRange(competition.competitions_info) }}
                </span>
                <span v-if="competition.competitions_info?.country" class="d-flex align-items-center gap-1">
                  <span class="flag" aria-hidden="true">{{ competition.competitions_info.country.emoji || competition.competitions_info.country.iso2 }}</span>
                  <span>{{ competition.competitions_info.country.name }}</span>
                </span>
              </div>

              <div class="meta-line d-flex justify-content-center align-items-center gap-2 text-muted small">
                <template v-if="competition.is_open === 1">
                  <i class="fa-solid fa-lock-open text-success"></i>
                  <span>Open</span>
                </template>
                <template v-else-if="competition.is_open === 0">
                  <i class="fa-solid fa-lock text-secondary"></i>
                  <span>Invite only</span>
                </template>
                <template v-else>
                  <span>—</span>
                </template>
              </div>

              <div class="meta-line d-flex justify-content-between align-items-center text-muted small">
                <span class="d-flex align-items-center gap-1">
                  <i class="fa-solid fa-scroll"></i>
                  <span>{{ competition.rule?.name || '—' }}</span>
                </span>
                <span class="d-flex align-items-center gap-1">
                  <i class="fa-solid fa-shapes"></i>
                  <span>{{ competition.type?.name || '—' }}</span>
                </span>
              </div>

              <div class="meta-line d-flex justify-content-between align-items-center text-muted small">
                <span class="d-flex align-items-center gap-1">
                  <i class="fa-solid fa-trophy text-warning"></i>
                  <span>{{ formatPrize(competition.competitions_info?.prize_amount, competition) }}</span>
                </span>
                <span class="d-flex align-items-center gap-1">
                  <i class="fa-solid fa-ticket text-primary"></i>
                  <span>{{ formatFee(competition.competitions_info?.fees, competition) }}</span>
                </span>
              </div>
              <div v-if="competition.publish_at && !competition.started_at" class="countdown-box">
                <CountDown
                    :competition_date="competition.competitions_info?.date_start || null"
                    :competition_timezone="competition.competitions_info?.country_timezone || competition.competitions_info?.country?.timezones || null"
                    :competition_list="true"
                />
              </div>
              <div v-if="competition.publish_at && !competition.started_at" class="athlete-summary-bar">
                <AthleteSummaryBar
                    v-if="canManageCompetition(competition) && !competition.started_at"
                    :competition="competition"
                    :compact="true"
                />
              </div>
              <div v-if="!competition.approved " class="progress-container">
                <div
                    class="progress-bar"
                    role="progressbar"
                    :style="{ width: competition.completion_percentage + '%' }"
                    :aria-valuenow="competition.completion_percentage"
                    aria-valuemin="0"
                    aria-valuemax="100"
                >
                  {{ competition.completion_percentage }}%
                </div>
              </div>
            </div>
            <div class="card-footer" @click.stop>
                <button
                  v-if="isSupervisor(this.user) && !canManageCompetition(competition)"
                  :disabled="competition.status_id !== 3 || competition.approved"
                  class="action-btn approve"
                  type="button"
                  :title="$t('competitions.btn_approve')"
                  @click.prevent="approveCompetition(competition.id)"
              >
                <i class="fa-solid fa-check"></i>
              </button>
              <button
                  v-else-if="canManageCompetition(competition) && !competition.approved && competition.status_id !== 3"
                  class="action-btn notify"
                  type="button"
                  :disabled="competition.completion_percentage < 100"
                  :title="$t('competitions.request_validation')"
                  @click.prevent="openRequestApproval(competition.id)"
              >
                <i class="fa-solid fa-paper-plane"></i>
              </button>
              <button
                  v-else-if="canManageCompetition(competition)"
                  :disabled="competition.publish_at || !competition.approved"
                  class="action-btn notify"
                  type="button"
                  @click.prevent="notifyCompetition(competition.id)"
                  :title="competition.completion_percentage < 100 ?  this.$t('competitions.publish_complete') : this.$t('competitions.btn_publish')"
              >
                <i class="fa-solid fa-upload"></i>
              </button>
              <button
                  v-if="canManageCompetition(competition)"
                  :disabled="competition.approved || competition.status_id === 3"
                  class="action-btn edit"
                  type="button"
                  :title="$t('competitions.btn_edit')"
                  @click.prevent="editCompetition(competition.id)"
              >
                <i class="fa-solid fa-pencil-alt"></i>
              </button>
              <button
                  v-if="canManageCompetition(competition)"
                  :disabled="competition.publish_at"
                  class="action-btn delete"
                  type="button"
                  :title="$t('competitions.btn_delete')"
                  @click.prevent="openDeleteModal(competition.id)"
              >
                <i class="fa-solid fa-trash"></i>
              </button>
            </div>
          </div>
      </template>
    </div>
    <div class="pagination-controls" v-if="totalPages > 1">
      <button class="pagination-btn" @click="changePage(currentPage - 1)" :disabled="currentPage === 1">
        {{ $t('competitions.pagination.prev') }}
      </button>

      <span class="pagination-text">
          {{ $t('competitions.pagination.page') }} {{ currentPage }} {{ $t('competitions.pagination.of') }} {{ totalPages }}
        </span>

      <button class="pagination-btn" @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages">
        {{ $t('competitions.pagination.next') }}
      </button>
    </div>
  </div>
  <CameraModal
    :visible="showTechModal"
    :keepStreamOnHide="false"
    :title="$t('judge.camera.modal_title')"
    :helper="$t('judge.camera.helper')"
    :selectLabel="$t('judge.camera.select')"
    :noDevicesLabel="$t('judge.camera.no_devices')"
    :previewPlaceholder="$t('judge.camera.preview_placeholder')"
    :startCameraLabel="$t('judge.camera.start')"
    :recordLabel="$t('judge.camera.record')"
    :stopLabel="$t('judge.camera.stop')"
    :cancelLabel="''"
    :loadingLabel="$t('judge.camera.loading')"
    :savingLabel="$t('judge.camera.saving')"
    :noDataLabel="$t('judge.camera.no_data')"
    :stopCameraLabel="$t('judge.camera.stop_camera')"
    :qualityLabel="$t('judge.camera.quality_label')"
    :qualityLowLabel="$t('judge.camera.quality_low')"
    :qualityMediumLabel="$t('judge.camera.quality_medium')"
    :qualityMaxLabel="$t('judge.camera.quality_max')"
    :qualityProLabel="$t('judge.camera.quality_pro')"
    :statusRecordingLabel="$t('judge.camera.status_recording')"
    :statusReadyLabel="$t('judge.camera.status_ready')"
    :statusOffLabel="$t('judge.camera.status_off')"
    :actualLabel="$t('judge.camera.info_actual')"
    :targetLabel="$t('judge.camera.info_target')"
    :supportsLabel="$t('judge.camera.info_supports')"
    :autoLabel="$t('judge.camera.info_auto')"
    :styleLabel="$t('judge.camera.style_label')"
    :styleLightLabel="$t('judge.camera.style_light')"
    :styleDarkLabel="$t('judge.camera.style_dark')"
    :styleCustomLabel="$t('judge.camera.style_custom')"
    :authToken="token"
    :recordingFileName="techRecordingFileName"
    :brandLogo="techBrandLogoPrimary"
    :brandLogoAlt="techBrandLogoAlternate"
    :brandLogoHasBadge="techBrandLogoHasBadge"
    :competitionName="techCompetitionName"
    :athleteName="techOverlay.athleteName"
    :athleteCountryName="techOverlay.athleteCountryName"
    :athleteCountryCode="techOverlay.athleteCountryCode"
    :athleteFlagUrl="techOverlay.athleteFlagUrl"
    :stageTitle="techOverlay.stageTitle"
    :stageSubtitle="techOverlay.stageSubtitle"
    :vsLeftName="techOverlay.vsLeftName"
    :vsLeftFlagUrl="techOverlay.vsLeftFlagUrl"
    :vsRightName="techOverlay.vsRightName"
    :vsRightFlagUrl="techOverlay.vsRightFlagUrl"
    :startTrigger="techStartTrigger"
    :showRecordControls="false"
    :canRecord="false"
    @begin-recording="handleTechBegin"
    @finish-recording="handleTechFinish"
    @camera-ready="handleTechCameraReady"
    @camera-stopped="handleTechCameraStopped"
    @cancel="closeTechPreview"
  />
  <!-- Modal de advertencia -->
  <ConfirmationModal
      ref="confirmationModal"
      :message="modalMessage"
      :showCancel="showCancel"
      @confirmed="confirmModal"
      @canceled="cancelModal"
  />
  <loading-modal ref="loadingModal"></loading-modal>
</template>

<script>
import LoadingModal from "@/services/LoadingModal.vue";
import {mapActions, mapState} from "vuex";
import ConfirmationModal from "@/components/competitions/ConfirmationModal.vue";
import CountDown from "@/components/competitions/CountDown.vue";
import AthleteSummaryBar from "@/components/competitions/AthleteSummaryBar.vue";
import CameraModal from "@/components/recording/CameraModal.vue";
import {isOrganizer, isOwnerOrganizer, isSupervisor} from '@/helpers/roleUtils';
import {
  getCompetitionDateParts,
  parseCompetitionDate,
  resolveCompetitionTimezone,
} from '@/helpers/timezoneDate';
import router from "@/router/router";
import echoService from "@/services/echoService";
import {resolveResponsiveGridItemsPerPage} from "@/helpers/responsiveGridPagination";
import {secureStorage} from "@/services/secureStorage";

export default {
  name: "CompetitionsList",
  components: {AthleteSummaryBar, CountDown, ConfirmationModal, LoadingModal, CameraModal },
  data() {
    return {
      showCancel: false,
      modalMessage:"",
      selectedCompetitionId: null,
      selectedAction: null,
      searchQuery: "",
      selectedYear: "",
      selectedMonth: "",
      selectedStatus: "",
      selectedRule: "",
      selectedCountry: "",
      selectedType: "",
      selectedDate: "",
      isLoading: false,
      defaultImage: require("@/assets/competition.jpg"),
      currentPage: 1,
      itemsPerPage: 6,
      pagination: {
        current_page: 1,
        per_page: 6,
        total: 0,
        last_page: 1,
      },
      filterOptions: {
        statuses: [],
        rules: [],
        countries: [],
        years: [],
        months: [],
        types: [],
      },
      resizeObserver: null,
      resizeFrame: null,
      observedContainerWidth: 0,
      searchDebounce: null,
      competitionRefreshTimer: null,
      filtersRequestInFlight: false,
      showTechModal: false,
      techStartTrigger: 0,
      techRecordingFileName: 'tech-preview.webm',
      techBrandLogoPrimary: require("@/assets/logos/logo_pill_white.png"),
      techBrandLogoAlternate: require("@/assets/logos/logo_pill_black.png"),
      techBrandLogoHasBadge: true,
      techCompetitionName: 'Technical Preview',
      techOverlay: {
        athleteName: '',
        athleteCountryName: '',
        athleteCountryCode: '',
        athleteFlagUrl: '',
        stageTitle: '',
        stageSubtitle: '',
        vsLeftName: '',
        vsLeftFlagUrl: '',
        vsRightName: '',
        vsRightFlagUrl: '',
      },
    };
  },
  computed: {
    ...mapState('user', ['isAuthenticated','user','token']),
    ...mapState('competition', ['competitionIdToEdit', 'competitionIdToView',"appNavigation"]),
    ...mapState('competition_users', ['competitionUsers']),
    ...mapState('competition_list', ['competitionList']),
    ...mapState('training', ['trainingRounds','trainingRoundsIdToView','trainingRoundsIdToCreate','trainingRoundsIdToEdit']),
    ...mapState("round",["roundAthleteId","roundIdToUpdate","roundIdToView","competitionRounds","roundCompetitionIdToCreate","roundCompetitionIdToView","roundCompetitionIdToEdit","roundCompetitionIdToCreate","competitionPodiums","competitionRounds"]),
    uniqueStatuses() {
      return this.filterOptions.statuses || [];
    },
    uniqueRules() {
      return this.filterOptions.rules || [];
    },
    uniqueCountries() {
      return this.filterOptions.countries || [];
    },
    uniqueYears() {
      return this.filterOptions.years || [];
    },
    monthOptions() {
      return this.filterOptions.months || [];
    },
    uniqueTypes() {
      return this.filterOptions.types || [];
    },

    totalPages() {
      return Math.max(1, Number(this.pagination.last_page) || 1);
    },
    isTechOperator() {
      // Mostrar el botón si el usuario tiene flag de tech_operator activo
      const techFlag = this.isTruthyFlag(this.user?.tech_operator) || this.isTruthyFlag(this.user?.pivot?.tech_operator);
      return !!techFlag;
    }
  },
  async mounted() {
    await this.$nextTick();
    this.syncItemsPerPage();
    this.restoreCompetitionListNavigation();
    this.setupResponsivePagination();
    await this.fetchCompetitions();
    this.fetchCompetitionFilters();
    // Paso 2: Escuchar solo eventos relacionados a esta competición
    await this.clearCompetitionUsers();
    this.clearAppNavigationZone("competition");
    this.clearCompetition();
    this.clearRound();
    this.clearPodium();
    this.clearCompetitionIdToView();
    this.clearCompetitionIdToEdit();
    this.clearTrainingRounds();
    this.clearTrainingRoundsIdToView();
    this.clearTrainingRoundsIdToCreate();
    this.clearTrainingRoundsIdToEdit();
    this.clearCompetitionRoundIdToView();
    this.clearCompetitionRoundIdToCreate();
    this.clearCompetitionRoundIdToEdit();
    localStorage.removeItem('validatedTricks');
    localStorage.removeItem('selectedTricks');
    localStorage.removeItem('comboCount');
    localStorage.removeItem('videoUrl');

    echoService.channel(`competition.global`)
        .listen('.CompetitionListUpdated', (e) => {
          const user = this.user
          const source = String(e?.source || '').trim().toLowerCase();
          const incomingCompetition = e?.competition_list;
          const hasLoadedList = Array.isArray(this.competitionList) && this.competitionList.length > 0;
          const existsInCurrentPage = hasLoadedList && this.competitionList.some(c => Number(c?.id) === Number(incomingCompetition?.id));

          if (source === 'live-channel') {
            if (existsInCurrentPage) {
              this.updateCompetitionList(incomingCompetition);
            }
            return;
          }

          if (source === 'competition-form') {
            if (existsInCurrentPage) {
              this.updateCompetitionList(incomingCompetition);
            }
            return;
          }

          if (!user || !Array.isArray(user.roles)) {
            return;
          }
          const isOrganizer = this.canManageCompetition(e.competition_list);
          const isSupervisor = user.roles.some(r => r.name === 'Supervisor')
          const haveAccess = isOrganizer || isSupervisor;
          if (haveAccess && !e.competition_list.publish_at) {
            this.scheduleCompetitionRefresh();
          }else if(e.competition_list.publish_at){
            this.scheduleCompetitionRefresh();
          }
        })
        .listen('.AthleteSummaryListUpdated', (e) => {
          const { summary, competitionId } = this.normalizeSummaryPayload(e);
          if (!summary || !competitionId) return;

          const safeSummary = this.enrichSummary(summary, competitionId);

          // 1. Guardamos también en el módulo separado si querés seguir usándolo
          this.setAthleteSummaryBarFromList({
            competitionId,
            summary: safeSummary
          });

          // 2. Buscamos la competición correspondiente en competitionList
          const index = this.competitionList.findIndex(c => c.id === competitionId);
          if (index !== -1) {
            const updatedCompetition = {
              ...this.competitionList[index],
              athletes_summary: safeSummary
            };

            this.updateCompetitionList(updatedCompetition);
          }
        })
        .error(error => {
          console.error('❌ Error en WebSocket:', error);
        });
  },
  beforeUnmount() {
    this.teardownResponsivePagination();
    if (this.searchDebounce) {
      window.clearTimeout(this.searchDebounce);
      this.searchDebounce = null;
    }
    if (this.competitionRefreshTimer) {
      window.clearTimeout(this.competitionRefreshTimer);
      this.competitionRefreshTimer = null;
    }
  },
  methods: {
    ...mapActions('competition',["setCompetitionIdToEdit","setCompetitionIdToView","updateAppNavigationZone","clearAppNavigationZone","clearCompetitionIdToView","clearCompetitionIdToEdit","clearCompetition"]),
    ...mapActions('competition_list',["setCompetitionList","updateCompetitionList"]),
    ...mapActions('athlete_summary',["setAthleteSummaryBarFromList"]),
    ...mapActions('competition_users',["clearCompetitionUsers"]),
    ...mapActions('training',["setTrainingRounds","setTrainingRoundsIdToView","setTrainingRoundsIdToCreate","setTrainingRoundsIdToEdit","clearTrainingRoundsIdToView","clearTrainingRoundsIdToCreate","clearTrainingRoundsIdToEdit","clearTrainingRounds"]),
    ...mapActions("round",["clearRoundIdToUpdate","clearRoundIdToView","clearRoundAthleteId","clearCompetitionRoundIdToCreate","clearCompetitionRoundIdToView","clearCompetitionRoundIdToEdit","clearRound","clearPodium"]),
    isOwnerOrganizer,
    isSupervisor,
    isOrganizer,
    canManageCompetition(competition) {
      if (typeof competition?.is_owner_organizer === "boolean") {
        return competition.is_owner_organizer;
      }

      return isOwnerOrganizer(this.user, competition);
    },
    normalizePageValue(value, fallback = 1) {
      const normalized = Number(value);
      return Number.isFinite(normalized) && normalized > 0 ? Math.floor(normalized) : fallback;
    },
    restoreCompetitionListNavigation() {
      const navigationState = this.appNavigation?.competitionList;
      if (!navigationState?.returnFromCompetitionView) {
        return;
      }

      const restoredItemsPerPage = this.normalizePageValue(navigationState.itemsPerPage, this.itemsPerPage);
      const restoredPage = this.normalizePageValue(navigationState.page, 1);
      const restoredOffset = Math.max(restoredPage - 1, 0) * restoredItemsPerPage;

      this.searchQuery = navigationState.searchQuery || "";
      this.selectedYear = navigationState.selectedYear || "";
      this.selectedMonth = navigationState.selectedMonth || "";
      this.selectedStatus = navigationState.selectedStatus || "";
      this.selectedRule = navigationState.selectedRule || "";
      this.selectedCountry = navigationState.selectedCountry || "";
      this.selectedType = navigationState.selectedType || "";
      this.currentPage = Math.max(1, Math.floor(restoredOffset / this.itemsPerPage) + 1);

      this.clearAppNavigationZone("competitionList");
    },
    getCompetitionListNavigationState() {
      return {
        returnFromCompetitionView: true,
        page: this.currentPage,
        itemsPerPage: this.itemsPerPage,
        searchQuery: this.searchQuery,
        selectedYear: this.selectedYear,
        selectedMonth: this.selectedMonth,
        selectedStatus: this.selectedStatus,
        selectedRule: this.selectedRule,
        selectedCountry: this.selectedCountry,
        selectedType: this.selectedType,
      };
    },
    safeParse(value) {
      if (typeof value !== 'string') return value;
      try {
        return JSON.parse(value);
      } catch (err) {
        console.warn('⚠️ No se pudo parsear el payload de summary', value, err);
        return null;
      }
    },
    normalizeSummaryPayload(payload) {
      const parsedEvent = this.safeParse(payload) || payload || {};
      const rawSummary = parsedEvent?.summary ?? parsedEvent?.data ?? null;
      const parsedSummary = this.safeParse(rawSummary) || rawSummary;
      const summary = this.safeParse(parsedSummary?.summary) || parsedSummary?.summary || parsedSummary;
      const competitionId = Number(
          parsedEvent?.competition_id ??
          parsedSummary?.competition_id ??
          summary?.competition_id ??
          parsedEvent?.competitionId ??
          parsedSummary?.competitionId ??
          summary?.competitionId
      );

      return { summary, competitionId: Number.isFinite(competitionId) ? competitionId : null };
    },
    enrichSummary(summary, competitionId) {
      const comp = this.competitionList.find(c => Number(c.id) === Number(competitionId)) || {};
      const fallbackMin = Number(comp?.athletes_min);
      const existingSummary = comp?.athletes_summary || {};
      const minFromExisting = Number(existingSummary?.min_approved);

      const min_approved = Number.isFinite(summary?.min_approved)
        ? summary.min_approved
        : Number.isFinite(fallbackMin) && fallbackMin > 0
          ? fallbackMin
          : Number.isFinite(minFromExisting) && minFromExisting > 0
            ? minFromExisting
            : summary?.active_total;

      const athlete_count = Number.isFinite(summary?.athlete_count)
        ? summary.athlete_count
        : summary?.active_total;

      return {
        ...summary,
        min_approved,
        athlete_count,
      };
    },
    setupResponsivePagination() {
      this.$nextTick(() => {
        this.scheduleItemsPerPageUpdate();
        window.addEventListener("resize", this.scheduleItemsPerPageUpdate);
        window.addEventListener("orientationchange", this.scheduleItemsPerPageUpdate);

        if (typeof ResizeObserver === "undefined") {
          return;
        }

        const target = this.$refs.boxComponent;
        if (!target) {
          return;
        }

        this.resizeObserver = new ResizeObserver((entries) => {
          const entry = entries?.[0];
          const nextWidth = Math.round(entry?.contentRect?.width || 0);

          if (!nextWidth || nextWidth === this.observedContainerWidth) {
            return;
          }

          this.observedContainerWidth = nextWidth;
          this.scheduleItemsPerPageUpdate();
        });
        this.resizeObserver.observe(target);
      });
    },
    teardownResponsivePagination() {
      window.removeEventListener("resize", this.scheduleItemsPerPageUpdate);
      window.removeEventListener("orientationchange", this.scheduleItemsPerPageUpdate);
      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = null;
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },
    scheduleItemsPerPageUpdate() {
      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
      }

      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.updateItemsPerPage();
      });
    },
    async updateItemsPerPage() {
      const nextItemsPerPage = this.resolveItemsPerPage();
      if (!nextItemsPerPage) {
        return;
      }

      if (nextItemsPerPage === this.itemsPerPage) {
        return;
      }

      const currentOffset = Math.max(this.currentPage - 1, 0) * this.itemsPerPage;
      this.itemsPerPage = nextItemsPerPage;
      this.currentPage = Math.max(1, Math.floor(currentOffset / this.itemsPerPage) + 1);
      await this.fetchCompetitions();
    },
    syncItemsPerPage() {
      const nextItemsPerPage = this.resolveItemsPerPage();
      if (!nextItemsPerPage) {
        return;
      }

      this.itemsPerPage = nextItemsPerPage;
      this.pagination = {
        ...this.pagination,
        per_page: nextItemsPerPage,
      };
    },
    resolveItemsPerPage() {
      const target = this.$refs.boxComponent;
      if (!target) {
        return null;
      }

      const containerWidth = Math.round(target.clientWidth || 0);
      if (!containerWidth) {
        return null;
      }

      this.observedContainerWidth = containerWidth;

      const responsiveItemsPerPage = resolveResponsiveGridItemsPerPage(containerWidth, window.innerWidth, {
        desktopMinCardWidth: 280,
        mobileMinCardWidth: 280,
        desktopGridGap: 15,
        mobileGridGap: 15,
        rowsPerPage: 2,
      });

      return Math.max(responsiveItemsPerPage, 6);
    },
    buildCompetitionQueryParams() {
      return {
        page: this.currentPage,
        per_page: this.itemsPerPage,
        search: this.searchQuery || undefined,
        year: this.selectedYear || undefined,
        month: this.selectedMonth || undefined,
        status: this.selectedStatus || undefined,
        rule: this.selectedRule || undefined,
        country: this.selectedCountry || undefined,
        type: this.selectedType || undefined,
      };
    },
    resolveAuthHeaders() {
      const authToken = this.token || secureStorage.getItem('setIn');
      return authToken ? { Authorization: `Bearer ${authToken}` } : {};
    },
    async fetchCompetitionFilters() {
      if (this.filtersRequestInFlight) {
        return;
      }

      const headers = this.resolveAuthHeaders();
      if (!headers.Authorization) {
        return;
      }

      this.filtersRequestInFlight = true;

      try {
        const response = await this.$axios.get("/competitions/list/filters", {
          params: {
            search: this.searchQuery || undefined,
            year: this.selectedYear || undefined,
            month: this.selectedMonth || undefined,
            status: this.selectedStatus || undefined,
            rule: this.selectedRule || undefined,
            country: this.selectedCountry || undefined,
            type: this.selectedType || undefined,
          },
          headers,
        });

        this.filterOptions = {
          ...this.filterOptions,
          ...(response.data.filters || {}),
        };
      } catch (error) {
        console.error("❌ Error al obtener los filtros de competiciones:", error);
      } finally {
        this.filtersRequestInFlight = false;
      }
    },
    onFilterChange() {
      this.currentPage = 1;
      this.fetchCompetitions();
      this.fetchCompetitionFilters();
    },
    onSearchInput() {
      this.currentPage = 1;
      if (this.searchDebounce) {
        window.clearTimeout(this.searchDebounce);
      }
      this.searchDebounce = window.setTimeout(() => {
        this.searchDebounce = null;
        this.fetchCompetitions();
        this.fetchCompetitionFilters();
      }, 250);
    },
    changePage(page) {
      const nextPage = Math.max(1, Math.min(page, this.totalPages));
      if (nextPage === this.currentPage) {
        return;
      }
      this.currentPage = nextPage;
      this.fetchCompetitions();
    },
    scheduleCompetitionRefresh() {
      if (this.competitionRefreshTimer) {
        window.clearTimeout(this.competitionRefreshTimer);
      }
      this.competitionRefreshTimer = window.setTimeout(() => {
        this.competitionRefreshTimer = null;
        this.fetchCompetitions();
      }, 150);
    },
    async fetchCompetitions() {
      const headers = this.resolveAuthHeaders();
      if (!headers.Authorization) {
        return;
      }

      this.showLoading();
      try {
        const response = await this.$axios.get("/competitions/list", {
          params: this.buildCompetitionQueryParams(),
          headers,
        });
        if (Array.isArray(response.data?.competitions)) {
          await this.setCompetitionList(response.data.competitions);
          this.pagination = {
            ...this.pagination,
            ...(response.data.pagination || {}),
          };
          this.currentPage = Number(response.data.pagination?.current_page) || this.currentPage;

          const lastPage = Number(response.data.pagination?.last_page) || 1;
          if (this.currentPage > lastPage) {
            this.currentPage = lastPage;
            await this.fetchCompetitions();
            return;
          }
        } else {
          console.warn("⚠️ No se encontraron competiciones en la respuesta de la API.");
          await this.setCompetitionList([]);
        }
      } catch (error) {
        console.error("❌ Error al obtener las competiciones:", error);
      } finally {
        this.hideLoading();
      }
    },
    getLoadingModalRef() {
      return this.$refs.loadingModal || this.$root?.$refs?.globalLoading || null;
    },
    showLoading() {
      this.isLoading = true;
      this.$nextTick(() => {
        const loadingModal = this.getLoadingModalRef();
        if (loadingModal?.showLoading) {
          loadingModal.showLoading();
        }
      });
    },
    hideLoading() {
      this.isLoading = false;
      this.$nextTick(() => {
        const loadingModal = this.getLoadingModalRef();
        if (loadingModal?.hideLoading) {
          loadingModal.hideLoading();
        }
      });
    },
    viewCompetition(id) {
      if (id) {
        this.updateAppNavigationZone({
          zone: "competitionList",
          data: this.getCompetitionListNavigationState(),
        });
        this.setCompetitionIdToView(id);
        router.push({
          path: '/competitionView',
          query: { id }
        });
      } else {
        console.warn("⚠️ No se recibió un ID válido para editar");
      }
    },
    async approveCompetition(id) {
      this.showLoading();
      const response = await this.fetchStatusUpdate('competitions', id, 4,'list');
      if (response.success) {
        this.hideLoading();
      } else {
        this.hideLoading();
        console.log('⚠️ Falló el envío');
      }
    },
    async notifyCompetition(id) {
      this.showLoading();
      const response = await this.fetchStatusUpdate('competitions', id, 10,'list');
      if (response.success) {
        this.hideLoading();
      } else {
        console.log('⚠️ Falló el envío');
        this.hideLoading();
      }
    },
    editCompetition(id) {
      if (id) {
        this.setCompetitionIdToEdit(id);
        router.push('/competition/assistant');
      } else {
        console.warn("⚠️ No se recibió un ID válido para editar");
      }
    },
    openDeleteModal(id) {
      this.modalMessage = this.$t('competitions.messages.delete_competition');
      this.showCancel = true;
      this.selectedCompetitionId = id; // Guardamos el ID de la competición
      this.selectedAction = 'delete';
      this.$refs.confirmationModal.openModal(); // 🔥 Usa `ref` para abrir el modal
    },
    openRequestApproval(id) {
      this.modalMessage = this.$t('competitions.assistant.messages.validate_confirm') || this.$t('competitions.request_validation_confirm') || 'Se enviará la solicitud de aprobación. ¿Confirmas?';
      this.showCancel = true;
      this.selectedCompetitionId = id;
      this.selectedAction = 'requestApproval';
      this.$refs.confirmationModal.openModal();
    },
    isDesktopDevice() {
      if (typeof navigator === 'undefined') return true;
      const ua = navigator.userAgent || '';
      const mobileRe = /(Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone|Opera Mini|IEMobile|Mobile|Tablet)/i;
      return !mobileRe.test(ua);
    },
    async confirmModal() {
      if (!this.selectedCompetitionId) return;
      if (this.selectedAction === 'delete') {
        this.showLoading();
        try {
          const response = await this.$axios.delete(`/competitions/${this.selectedCompetitionId}`, {
            headers: this.resolveAuthHeaders(),
          });
          if (response.status === 200 && response?.data) {
            await this.fetchCompetitions();
          } else {
            console.log('⚠️ Falló el envío');
          }
        } catch (error) {
          console.error('❌ Error al eliminar:', error.response ? error.response.data : error.message);
        } finally {
          this.hideLoading();
        }
      } else if (this.selectedAction === 'requestApproval') {
        await this.fetchStatusUpdate('competitions', this.selectedCompetitionId, 3, 'list');
        await this.fetchCompetitions();
      }
      this.selectedCompetitionId = null;
      this.selectedAction = null;
    },
    cancelModal() {
      this.selectedCompetitionId = null;
      this.selectedAction = null;
    },
    createNewCompetition() {
      router.push('/Competition');
    },
    openAssistant() {
      router.push('/competition/assistant');
    },
    async openTechPreview() {
      if (!this.isDesktopDevice()) {
        this.modalMessage = this.$t('competitions.recording_desktop_only') || 'La grabación solo está disponible en PC.';
        this.showCancel = false;
        this.selectedAction = null;
        this.$refs.confirmationModal.openModal();
        return;
      }
      this.showTechModal = true;
    },
    closeTechPreview() {
      this.showTechModal = false;
    },
    handleTechBegin() {},
    handleTechFinish() {},
    handleTechCameraReady() {},
    handleTechCameraStopped() {},
    async fetchStatusUpdate(entity, id, status_id, source){
      this.showLoading();
      try {
        const response = await this.$axios.post(`/status/update`, {
          entity: entity,
          id: id,
          new_status: status_id,
          source: source,
        },{
          headers: this.resolveAuthHeaders(),
        });
        if (response.status === 200 && response?.data) {
          return response.data;
        }
      } catch (error) {
        console.error("❌ Error al obtener la competencia:", error);
      } finally {
        this.hideLoading();
      }
    },
    getStatusClass(statusId) {
      switch (statusId) {
        case 3:
          return 'status-pending';      // Pendiente de aprobación
        case 4:
          return 'status-approved';     // Aprobado
        case 10:
          return 'status-published';    // Publicado
        case 15:
          return 'status-started';      // Empezado
        case 8:
          return 'status-finished';     // Finalizado
        default:
          return 'status-unknown';      // Fallback
      }
    },
    statusKey(statusId) {
      switch (statusId) {
        case 3:
          return 'pill-pending';
        case 4:
          return 'pill-approved';
        case 10:
          return 'pill-published';
        case 15:
          return 'pill-started';
        case 8:
          return 'pill-finished';
        default:
          return 'pill-unknown';
      }
    },
    formatDateRange(info) {
      if (!info?.date_start) return '—';

      const format = (d, withYear = true) => {
        const dd = String(d.day).padStart(2, '0');
        const mm = String(d.month).padStart(2, '0');
        const yy = String(d.year).slice(-2);
        return withYear ? `${dd}-${mm}-${yy}` : `${dd}`;
      };

      const competitionTimeZone = resolveCompetitionTimezone(info);
      const start = parseCompetitionDate(info.date_start, competitionTimeZone);
      if (!start || Number.isNaN(start.getTime())) return '—';
      const startParts = getCompetitionDateParts(start, competitionTimeZone);
      if (!startParts) return '—';

      const end = info.date_end ? parseCompetitionDate(info.date_end, competitionTimeZone) : start;
      if (!end || Number.isNaN(end.getTime())) return format(startParts);
      const endParts = getCompetitionDateParts(end, competitionTimeZone);
      if (!endParts) return format(startParts);

      if (startParts.year === endParts.year && startParts.month === endParts.month && startParts.day === endParts.day) {
        return format(startParts);
      }
      return `${format(startParts, false)}/${format(endParts)}`;
    },
    currencyPrefixFor(competition) {
      const candidates = [
        competition?.country?.currency_symbol,
        competition?.country?.prefix,
        competition?.competitions_info?.country?.currency_symbol,
        competition?.competitions_info?.country?.prefix,
        competition?.currency_symbol,
        competition?.currency?.symbol,
      ];

      const found = candidates.find((candidate) => typeof candidate === 'string' && candidate.trim().length);
      return found ? found.trim() : '';
    },
    formatCurrency(value, competition, { withPrefix = false } = {}) {
      const num = Number(value);
      if (!Number.isFinite(num)) return '—';
      const rounded = Math.round(num);
      const formatted = rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');

      if (!withPrefix) {
        return formatted;
      }

      const currencyPrefix = this.currencyPrefixFor(competition);
      if (!currencyPrefix) {
        return formatted;
      }

      return /[A-Za-z0-9]$/.test(currencyPrefix)
        ? `${currencyPrefix} ${formatted}`
        : `${currencyPrefix}${formatted}`;
    },
    formatPrize(value, competition) {
      const num = Number(value);
      if (!Number.isFinite(num)) return '—';
      const rounded = Math.round(num);
      if (rounded === 0) return this.$t('competitions.assistant.labels.prize_zero_badge');
      return this.formatCurrency(rounded, competition, { withPrefix: true });
    },
    formatFee(value, competition) {
      const num = Number(value);
      if (!Number.isFinite(num)) return '—';
      const rounded = Math.round(num);
      if (rounded === 0) return this.$t('competitions.view.free');
      return this.formatCurrency(rounded, competition, { withPrefix: true });
    },
    isTruthyFlag(flag) {
      return flag === true || flag === 'true' || flag === 1 || flag === '1' || Number(flag) === 1;
    }
  },
};
</script>

<style scoped>
.box_component {
  width: 100%;
  max-width: 100%;
  margin: 15px auto;
}
.loading-text {
  text-align: center;
  font-size: 16px;
  color: #666;
}
.filter-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
}
.search-input, .status-filter {
  flex: 1;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.competitions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 15px;
  justify-content: center;
  margin: 0 auto;
}
.competition-card {
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  text-align: center;
  background: #fff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: box-shadow 0.3s ease;
  margin-bottom: 15px;
  width: 100%;
  max-width: 320px;
  justify-self: center;
}

/* Estados con sombra de color sutil */
.status-pending {
  box-shadow: 0 0 10px rgba(255, 165, 0, 0.6); /* naranja claro */
}
.status-approved {
  box-shadow: 0 0 10px rgba(0, 128, 0, 0.6); /* verde */
}
.status-published {
  box-shadow: 0 0 10px rgba(0, 0, 255, 0.6); /* azul */
}
.status-started {
  box-shadow: 0 0 10px rgba(255, 215, 0, 0.6); /* dorado */
}
.status-finished {
  box-shadow: 0 0 10px rgba(128, 128, 128, 0.6); /* gris */
}

.card-header {
  position: relative; /* Permite superponer elementos */
  width: 100%;
  overflow: hidden;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.status-line {
  margin-bottom: 6px;
  text-align: left;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.9rem;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08);
}

.pill-pending { background: #fff7e6; color: #b86b00; }
.pill-approved { background: #e6f7ff; color: #006699; }
.pill-published { background: #f0e8ff; color: #5c3aff; }
.pill-started { background: #fff4d6; color: #d97706; }
.pill-finished { background: #e7f6e7; color: #2f855a; }
.pill-unknown { background: #f2f2f2; color: #555; }

.card-title {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, -50%); /* Centra horizontal y verticalmente */
  background: rgba(255, 255, 255, 0.8); /* Fondo blanco semitransparente */
  padding: 4px 10px;
  font-size: 24px;

.pill.muted {
  background: #f5f5f5;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 0.85rem;
  color: #555;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 6px;
  text-align: center;
}

.meta-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
  width: 100%;
}
  font-weight: bold;
  max-width: 90%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis; /* Trunca el texto largo */
  border-radius: 4px;
  text-align: center;
}
.card-image {
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  opacity: 0.7;
}
.card-body {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  padding: 10px;
}

.meta-line {
  margin-top: 4px;
}

.flag {
  font-size: 18px;
  line-height: 1;
}
.card-footer {
  display: flex;
  justify-content: space-around;
  padding: 10px;
  background: #f8f9fa;
  border-top: 1px solid #ddd;
}
.action-btn {
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  padding: 5px;
  transition: color 0.3s ease;
}
.action-btn.view { color: #5b5b5b; }
.action-btn.notify { color: #5b5b5b; }
.action-btn.edit { color: #5b5b5b; }
.action-btn.delete { color: #5b5b5b; }
.action-btn.approve { color: #5b5b5b; }
.action-btn:hover {
  opacity: 0.7;
}
.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.action-btn:disabled i {
  color: gray !important;
}
.progress-container {
  width: 100%;
  background-color: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  margin-top: auto;
}
.progress-bar {
  height: 15px;
  background-color: #28a745;
  text-align: center;
  font-size: 12px;
  color: white;
  line-height: 10px;
}
.header-title {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}
.header-title h2 {
  margin: 0;
  margin-right: auto;
}
.new-competition-btn {
  background-color: #28a745;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
}
.new-competition-btn:hover {
  background-color: #218838;
}
.no-results {
  text-align: center;
  color: #999;
  font-size: 14px;
  margin-top: 20px;
}
.filter-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}
.search-input, .status-filter, .gender-filter, .rule-filter, .type-filter, .date-filter {
  flex: 1;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.countdown-box {
  width: 100%;
  display: flex;
  justify-content: center;
  z-index: 1000;
}
.athlete-summary-bar {
  width: 100%;
  display: flex;
  justify-content: center;
  z-index: 1000;
}
.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  flex-wrap: wrap;
}

.pagination-btn {
  background-color: #4c56af;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #3b4491;
}

.pagination-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.pagination-text {
  font-size: 14px;
  color: #444;
}

.tech-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1200;
  padding: 16px;
}
.tech-modal {
  background: #fff;
  border-radius: 12px;
  width: min(960px, 96vw);
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 16px 40px rgba(0,0,0,0.22);
}
.tech-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  border-bottom: 1px solid #e5e7eb;
}
.tech-close {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
}
.tech-modal-body {
  padding: 12px 14px 18px;
}

.link-plain {
  text-decoration: none;
  border: none;
  outline: none;
  color: inherit;
  background: none;
}
@media (max-width: 576px) {
  .competition-card{
    width: 100%;
    max-width: 320px;
    margin: 0 auto;
  }
}
</style>